import { FC, useState } from 'react';

import { IPolicySettingsField } from '@entities/policy';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Stack, Typography } from '@mui/material';
import { SelectMulti } from '@shared/components/Form';

import {
  DIGIT_OPERATORS,
  IDigitParam,
  IMultiParam,
  ITagDigitParam,
  TDigitOperator,
  TRuleParam,
  newRuleParam,
} from './ruleParamInputs';
import { toOptions } from './selectOptions';

interface IProps {
  title: string;
  items: TRuleParam[];
  // The dictionary for this section (conditions or selections) from GET /router/settings.
  fields: IPolicySettingsField[];
  onChange: (items: TRuleParam[]) => void;
}

// Count params must be ≥ 1, except the ones the engine lets go to zero.
const ZERO_ALLOWED_COUNT_PARAMS = new Set([
  'active_wallets_count',
  'active_boi_wallets_count',
  'active_wallets_in_tag_count',
  'active_wallets_not_in_tag_count',
]);

const resolveMin = (techname: string, label: string) => {
  const isCount = /count/i.test(techname) || /count/i.test(label);
  if (!isCount) return undefined;
  return ZERO_ALLOWED_COUNT_PARAMS.has(techname.toLowerCase()) ? 0 : 1;
};

// The Policies page's rule-form section: "Add Param" from the dictionary, then one
// card per param with the controls its type needs. Local state only.
const RuleParamsSection: FC<IProps> = ({ title, items, fields, onChange }) => {
  const [pickedTechname, setPickedTechname] = useState('');
  const available = fields.filter(field => !items.some(item => item.techname === field.techname));
  const picked = available.find(field => field.techname === pickedTechname);
  const emptyMessage = /condition/i.test(title)
    ? 'No condition parameters added. Click "Add Param" to specify conditions.'
    : 'No selection parameters added. Click "Add Param" to specify targeting.';

  const add = (field: IPolicySettingsField) => {
    onChange([...items, newRuleParam(field)]);
    setPickedTechname('');
  };

  const update = (techname: string, patch: Partial<ITagDigitParam>) =>
    onChange(
      items.map(item => (item.techname === techname ? ({ ...item, ...patch } as TRuleParam) : item)),
    );

  const remove = (techname: string) => onChange(items.filter(item => item.techname !== techname));

  const renderTags = (param: IMultiParam | ITagDigitParam, values: Record<string, string>) => (
    <SelectMulti
      name={param.techname}
      options={toOptions(values, param.selected)}
      value={param.selected.map(value => ({ value, label: values[value] ?? value }))}
      onChange={selected =>
        update(param.techname, { selected: selected.map(option => option.value) })
      }
    />
  );

  const renderComparator = (param: IDigitParam | ITagDigitParam, label: string) => (
    <Stack direction='row' spacing={1} alignItems='center'>
      <select
        aria-label={`${label} operator`}
        value={param.operator}
        onChange={event =>
          update(param.techname, { operator: event.target.value as TDigitOperator })
        }
      >
        {DIGIT_OPERATORS.map(operator => (
          <option key={operator} value={operator}>
            {operator}
          </option>
        ))}
      </select>
      <input
        type='number'
        aria-label={`${label} value`}
        min={resolveMin(param.techname, label)}
        value={param.value}
        onChange={event => update(param.techname, { value: event.target.value })}
      />
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Typography variant='subtitle2' fontWeight={600}>
        {title}
      </Typography>

      <Stack direction='row' spacing={1} alignItems='center'>
        <select
          aria-label={`${title} param`}
          value={pickedTechname}
          onChange={event => setPickedTechname(event.target.value)}
        >
          <option value=''>Add Param</option>
          {available.map(field => (
            <option key={field.techname} value={field.techname}>
              {field.label}
            </option>
          ))}
        </select>
        <button
          type='button'
          className='secondary-btn routing-rule-params-add-btn'
          disabled={!picked}
          onClick={picked ? () => add(picked) : undefined}
        >
          Add Param
        </button>
      </Stack>

      {items.length === 0 && (
        <Typography variant='caption' color='text.secondary' textAlign='center'>
          {emptyMessage}
        </Typography>
      )}

      {items.map(param => {
        const field = fields.find(item => item.techname === param.techname);
        const label = field?.label ?? param.techname;
        const values = field?.values ?? {};

        return (
          <Stack
            key={param.techname}
            spacing={1}
            sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}
            data-testid='rule-param'
          >
            <Stack direction='row' alignItems='center' justifyContent='space-between'>
              <Typography variant='caption'>{label}</Typography>
              <IconButton
                size='small'
                color='error'
                aria-label={`Remove ${label}`}
                onClick={() => remove(param.techname)}
              >
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Stack>

            {param.type === 'multi-select' && renderTags(param, values)}
            {param.type === 'digit-compare' && renderComparator(param, label)}
            {param.type === 'tag-digit-compare' && (
              <Stack spacing={1}>
                {renderTags(param, values)}
                {renderComparator(param, label)}
              </Stack>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default RuleParamsSection;
